import type { ComponentProps, ReactNode } from "react";

import { Button } from "../components/Button";
import { Avatar, Badge } from "../components/Display";
import { Icon } from "../components/Icon";
import { Spinner } from "../components/Spinner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/Overlays";
import { cn } from "../lib/cn";

// ── LanguageSwitcher ─────────────────────────────────────────────────

export interface LanguageOption {
  code: string;
  label: string;
  /** Languages that exist but aren't enabled yet are shown disabled ("coming soon"). */
  enabled: boolean;
}

export interface LanguageSwitcherProps {
  value: string;
  languages: LanguageOption[];
  onChange: (code: string) => void;
  label?: string;
}

export function LanguageSwitcher({
  value,
  languages,
  onChange,
  label = "Language",
}: LanguageSwitcherProps) {
  const current = languages.find((l) => l.code === value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label={label} className="gap-1.5">
          <Icon name="globe" size={16} />
          <span className="hidden sm:inline">{current?.label ?? value.toUpperCase()}</span>
          <Icon name="chevron-down" size={14} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-40">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            disabled={!language.enabled}
            onSelect={() => onChange(language.code)}
          >
            <span className="flex-1">{language.label}</span>
            {language.code === value && <Icon name="check" size={16} />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── NotificationBell ─────────────────────────────────────────────────

export interface NotificationBellProps extends Omit<ComponentProps<typeof Button>, "children"> {
  count?: number;
  label?: string;
}

/** The bell button with a red unread badge. Extra props reach the button (so it can be a menu trigger). */
export function NotificationBell({
  count = 0,
  label = "Notifications",
  className,
  ...props
}: NotificationBellProps) {
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={count ? `${label} (${count})` : label}
      className={cn("relative", className)}
      {...props}
    >
      <Icon name="bell" />
      {count > 0 && (
        <span className="absolute top-1 right-1 flex min-w-4.5 items-center justify-center rounded-full bg-danger px-1 text-[0.65rem] leading-4.5 font-bold text-danger-foreground">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Button>
  );
}

// ── NotificationsMenu ────────────────────────────────────────────────

export interface NotificationMenuItem {
  id: string;
  title: string;
  body?: string;
  /** Already formatted, e.g. "5 min ago". */
  time: string;
  unread: boolean;
}

export interface NotificationsMenuProps {
  count: number;
  items: NotificationMenuItem[];
  status: "loading" | "error" | "ready";
  onSelect: (item: NotificationMenuItem) => void;
  onMarkAllRead?: () => void;
  /** e.g. refetch the list when the menu opens. */
  onOpenChange?: (open: boolean) => void;
  labels: {
    title: string;
    empty: string;
    error: string;
    markAllRead: string;
    unread: string;
  };
}

/** The bell with its drop-down list of recent notifications (data comes from the caller). */
export function NotificationsMenu({
  count,
  items,
  status,
  onSelect,
  onMarkAllRead,
  onOpenChange,
  labels,
}: NotificationsMenuProps) {
  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <NotificationBell count={count} label={labels.title} />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[min(22rem,calc(100vw-2rem))] p-0">
        <div className="flex items-center justify-between gap-2 border-b border-border px-3.5 py-2.5">
          <p className="text-sm font-semibold">{labels.title}</p>
          {onMarkAllRead && count > 0 && (
            <Button variant="link" size="sm" className="h-auto px-0" onClick={onMarkAllRead}>
              {labels.markAllRead}
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto p-1.5">
          {status === "loading" && (
            <div className="flex justify-center py-8" role="status" aria-label={labels.title}>
              <Spinner />
            </div>
          )}
          {status === "error" && (
            <p className="px-2.5 py-6 text-center text-sm text-danger">{labels.error}</p>
          )}
          {status === "ready" && items.length === 0 && (
            <div className="flex flex-col items-center gap-2 px-2.5 py-8 text-center text-sm text-muted-foreground">
              <Icon name="bell" size={22} />
              {labels.empty}
            </div>
          )}
          {status === "ready" &&
            items.map((item) => (
              <DropdownMenuItem
                key={item.id}
                onSelect={() => onSelect(item)}
                className="items-start gap-3 py-2.5"
              >
                <span
                  className={cn(
                    "mt-1.5 size-2 shrink-0 rounded-full",
                    item.unread ? "bg-brand" : "bg-transparent",
                  )}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span
                    className={cn("block text-sm", item.unread ? "font-semibold" : "font-medium")}
                  >
                    {item.title}
                    {item.unread && <span className="sr-only"> ({labels.unread})</span>}
                  </span>
                  {item.body && (
                    <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">
                      {item.body}
                    </span>
                  )}
                  <span className="mt-1 block text-xs text-muted-foreground">{item.time}</span>
                </span>
              </DropdownMenuItem>
            ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── UserMenu ─────────────────────────────────────────────────────────

export interface UserMenuItem {
  id: string;
  label: ReactNode;
  icon?: Parameters<typeof Icon>[0]["name"];
  onSelect: () => void;
  tone?: "default" | "danger";
}

export interface UserMenuProps {
  name: string;
  email: string;
  /** e.g. role or store name */
  caption?: ReactNode;
  items: UserMenuItem[];
  className?: string;
}

export function UserMenu({ name, email, caption, items, className }: UserMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center gap-2.5 rounded-full p-0.5 pr-2 hover:bg-foreground/5",
            className,
          )}
          aria-label={name}
        >
          <Avatar name={name || email} size="sm" />
          <span className="hidden max-w-36 truncate text-sm font-medium md:block">
            {name || email}
          </span>
          <Icon name="chevron-down" size={14} className="hidden md:block" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <div className="px-2.5 py-2">
          <p className="truncate text-sm font-semibold">{name || email}</p>
          <p className="truncate text-xs text-muted-foreground">{email}</p>
          {caption && (
            <Badge tone="brand" className="mt-1.5">
              {caption}
            </Badge>
          )}
        </div>
        <DropdownMenuSeparator />
        {items.map((item, index) => (
          <div key={item.id}>
            {item.tone === "danger" && index > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem tone={item.tone} onSelect={item.onSelect}>
              {item.icon && <Icon name={item.icon} size={16} />}
              {item.label}
            </DropdownMenuItem>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
